import React, { useEffect, useState } from 'react';
import api from '../api';
import { useParams, useNavigate } from 'react-router-dom';

export default function PurchaseBillItemsPage() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    Description: '',
    HSN: '',
    Size: '',
    Quantity: 1,
    Rate: '',
    GSTPercent: '',
  });
  const [bill, setBill] = useState(null);

  useEffect(() => {
    fetchItems();
    fetchBill();
  }, []);

  const fetchBill = async () => {
    const res = await api.get('/purchase-bills');
    const found = res.data.find(b => b.PurchaseBillID === parseInt(billId));
    setBill(found);
  };

  const fetchItems = async () => {
    const res = await api.get(`/purchase-bills/${billId}/items`);
    setItems(res.data);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Auto-calculated fields
  const amount = (form.Quantity && form.Rate) ? parseFloat(form.Quantity) * parseFloat(form.Rate) : 0;
  const cgst = (form.GSTPercent ? amount * (parseFloat(form.GSTPercent) / 2) / 100 : 0);
  const sgst = (form.GSTPercent ? amount * (parseFloat(form.GSTPercent) / 2) / 100 : 0);
  const igst = 0; // You can add IGST calculation if required for interstate
  const total = amount + cgst + sgst + igst;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.Description || !form.Quantity || !form.Rate || !form.GSTPercent) {
      alert('Fill required fields');
      return;
    }
    await api.post(`/purchase-bills/${billId}/items`, {
      items: [{
        ...form,
        Amount: amount,
        CGST: cgst,
        SGST: sgst,
        IGST: igst,
      }]
    });
    setForm({ Description: '', HSN: '', Size: '', Quantity: 1, Rate: '', GSTPercent: '' });
    fetchItems();
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Delete this item?')) {
      await api.delete(`/purchase-items/${itemId}`);
      fetchItems();
    }
  };

  // Totals
  const totalAmount = items.reduce((sum, i) => sum + parseFloat(i.Amount || 0), 0);
  const totalCGST = items.reduce((sum, i) => sum + parseFloat(i.CGST || 0), 0);
  const totalSGST = items.reduce((sum, i) => sum + parseFloat(i.SGST || 0), 0);
  const totalIGST = items.reduce((sum, i) => sum + parseFloat(i.IGST || 0), 0);
  const totalGST = totalCGST + totalSGST + totalIGST;
  const grandTotal = totalAmount + totalGST - parseFloat(bill?.Discount || 0);

  // Update bill total in DB whenever items change (optional, but best practice)
  useEffect(() => {
    if (bill) {
      api.put(`/purchase-bills/${billId}`, {
        ...bill,
        TotalAmount: grandTotal
      });
    }
    // eslint-disable-next-line
  }, [items.length]);

  return (
    <div className="container mt-4">
      <h2>Manage Items for Purchase Bill #{bill?.BillNo || billId}</h2>

      <form className="row g-2 mb-3" onSubmit={handleSubmit}>
        <div className="col-md-3">
          <input className="form-control" name="Description" placeholder="Description" value={form.Description} onChange={handleChange} required />
        </div>
        <div className="col-md-2">
          <input className="form-control" name="HSN" placeholder="HSN" value={form.HSN} onChange={handleChange} />
        </div>
        <div className="col-md-2">
          <input className="form-control" name="Size" placeholder="Size" value={form.Size} onChange={handleChange} />
        </div>
        <div className="col-md-1">
          <input className="form-control" name="Quantity" type="number" min="1" placeholder="Qty" value={form.Quantity} onChange={handleChange} required />
        </div>
        <div className="col-md-1">
          <input className="form-control" name="Rate" type="number" placeholder="Rate" value={form.Rate} onChange={handleChange} required />
        </div>
        <div className="col-md-1">
          <input className="form-control" name="GSTPercent" type="number" placeholder="GST%" value={form.GSTPercent} onChange={handleChange} required />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-primary w-100" type="submit">Add Item</button>
        </div>
      </form>

      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Description</th>
            <th>HSN</th>
            <th>Size</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>GST%</th>
            <th>Amount</th>
            <th>CGST</th>
            <th>SGST</th>
            <th>IGST</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(i => (
            <tr key={i.PurchaseItemID}>
              <td>{i.Description}</td>
              <td>{i.HSN}</td>
              <td>{i.Size}</td>
              <td>{i.Quantity}</td>
              <td>{parseFloat(i.Rate).toFixed(2)}</td>
              <td>{parseFloat(i.GSTPercent).toFixed(2)}</td>
              <td>{parseFloat(i.Amount).toFixed(2)}</td>
              <td>{parseFloat(i.CGST).toFixed(2)}</td>
              <td>{parseFloat(i.SGST).toFixed(2)}</td>
              <td>{parseFloat(i.IGST).toFixed(2)}</td>
              <td>
                <button className="btn btn-sm btn-danger" onClick={() => handleDelete(i.PurchaseItemID)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 'bold' }}>
            <td colSpan={6} className="text-end">Totals:</td>
            <td>{totalAmount.toFixed(2)}</td>
            <td>{totalCGST.toFixed(2)}</td>
            <td>{totalSGST.toFixed(2)}</td>
            <td>{totalIGST.toFixed(2)}</td>
            <td />
          </tr>
          <tr style={{ fontWeight: 'bold' }}>
            <td colSpan={6} className="text-end">Discount:</td>
            <td colSpan={2}>{parseFloat(bill?.Discount || 0).toFixed(2)}</td>
            <td colSpan={3}></td>
          </tr>
          <tr style={{ fontWeight: 'bold', color: 'green' }}>
            <td colSpan={6} className="text-end">Grand Total:</td>
            <td colSpan={5}>{grandTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      <button className="btn btn-secondary mt-3" onClick={() => navigate('/purchase-bills')}>← Back to Bills</button>
    </div>
  );
}
